import { CalendarClock, Plus } from "lucide-react";

import { ApiError } from "../api/client";

import { Badge, Card, EmptyState, Skeleton, cx } from "./ui";
import { formatDateTime, formatMoney } from "../lib/format";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import type { Lead } from "../api/types";

/**
 * Enquiries recorded against a contact (FR-6).
 *
 * Each row shows the price that was *quoted*, not the catalogue's current price.
 * That is the whole reason the lead carries a snapshot: a March enquiry must
 * still say 600 after the price becomes 700 in April, or nobody can tell what
 * the customer was actually told.
 */
export function LeadsCard({
  onAdd,
  adding,
  leads,
  pending,
  error,
}: {
  /** Fetched by the page, so the sidebar total and this list share a request. */
  leads: Lead[];
  pending: boolean;
  error: unknown;
  onAdd: () => void;
  adding: boolean;
}) {
  const timeZone = useAccountTimeZone();

  return (
    <Card>
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Leads</h2>
        <button
          onClick={onAdd}
          aria-expanded={adding}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus size={15} /> Add lead
        </button>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        What they have asked about. A tentative time holds nothing on the
        calendar.
      </p>

      {pending ? (
        <div className="space-y-3">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : error ? (
        <ErrorPanel error={error} />
      ) : leads.length === 0 ? (
        <EmptyState
          icon={<CalendarClock size={24} />}
          title="No leads yet"
          body="Record what someone is asking about and, if they mentioned one, when they might come in."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {leads.map((lead) => (
            <LeadRow key={lead.id} lead={lead} timeZone={timeZone} />
          ))}
        </ul>
      )}
    </Card>
  );
}

const STATUS_TONE: Record<string, string> = {
  new: "bg-sky-50 text-sky-700 ring-sky-600/20",
  contacted: "bg-amber-50 text-amber-700 ring-amber-600/20",
  qualified: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  unqualified: "bg-slate-100 text-slate-600 ring-slate-500/20",
  converted: "bg-violet-50 text-violet-700 ring-violet-600/20",
};

function ErrorPanel({ error }: { error: unknown }) {
  return (
    <EmptyState
      title="Could not load leads"
      body={error instanceof ApiError ? error.message : "Please try again."}
    />
  );
}

function LeadRow({ lead, timeZone }: { lead: Lead; timeZone: string | undefined }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-slate-900">
            {lead.product_name || "Unspecified enquiry"}
          </p>
          <span
            className={cx(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset",
              STATUS_TONE[lead.status ?? ""] ?? STATUS_TONE.unqualified,
            )}
          >
            {lead.status_display}
          </span>
          {/* FR-6.7: age is what makes a stale lead visible. */}
          {lead.is_open && lead.age_in_status >= 3 ? (
            <Badge tone="amber">{lead.age_in_status}d old</Badge>
          ) : null}
        </div>

        {lead.notes ? (
          <p className="mt-0.5 text-sm text-slate-600">{lead.notes}</p>
        ) : null}

        <p className="mt-1 text-xs text-slate-400">
          {lead.duration_minutes ? `${lead.duration_minutes} min` : ""}
          {lead.duration_minutes && lead.source ? " · " : ""}
          {lead.source ? lead.source.replace(/_/g, " ") : ""}
          {` · ${lead.age_in_status}d ago`}
        </p>
      </div>

      {/* What it is worth, above when they might come in. The money is the
          reason the enquiry exists; the date is only a possibility, and it is
          still only tentative. */}
      <div className="flex shrink-0 flex-col items-end gap-1">
        <p className="text-lg font-semibold tabular-nums tracking-tight text-slate-900">
          {lead.quoted_price ? formatMoney(lead.quoted_price) : "No price"}
        </p>
        {lead.requested_start_at ? (
          <p className="text-xs text-amber-700">
            Tentative {formatDateTime(lead.requested_start_at, timeZone)}
          </p>
        ) : null}
      </div>
    </li>
  );
}